import { fetchApiGateway } from "@nova/fetch-api-gateway"
import { CreatableMaintenanceRecord, UpdatableMaintenanceRecord } from "@nova/schemas"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { invalidatePilotTrackingQuery } from "features/pilot-tracking/PilotTrackingQueries"
import { useAppSelector } from "integrations/redux/hooks"
import useSWRMutation from "swr/mutation"
import { mutate } from "swr"
import { invalidateAircraftStatusCache } from "features/pilot-tracking/aircraft/AircraftStatusQueries"

const baseKey = "maintenance-record"

export const invalidateMaintenanceRecords = () => {
    mutate((key) => Array.isArray(key) && key[0] === baseKey)
    invalidatePilotTrackingQuery("maintenance_records")
    invalidateAircraftStatusCache()
}

const createMaintenanceRecord = ({
    body,
    orgId,
}: {
    body: CreatableMaintenanceRecord
    orgId: string
}) => {
    return fetchApiGateway("post /org/:orgId/fleet/maintenance/record", {
        body,
        params: { orgId },
    })
}

export const useCreateMaintenanceRecord = () => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { trigger, isMutating } = useSWRMutation(
        "create-maintenance-record",
        (key, { arg }: { arg: CreatableMaintenanceRecord }) =>
            orgId ? createMaintenanceRecord({ body: arg, orgId }) : undefined,
        { onSuccess: invalidateMaintenanceRecords }
    )

    return { createMaintenanceRecord: trigger, isMutating }
}

const updateMaintenanceRecord = ({
    recordId,
    body,
    orgId,
}: {
    recordId: string
    body: UpdatableMaintenanceRecord
    orgId: string
}) => {
    return fetchApiGateway("patch /org/:orgId/fleet/maintenance/record/:recordId", {
        body,
        params: { orgId, recordId },
    })
}

export const useUpdateMaintenanceRecord = () => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { trigger, isMutating } = useSWRMutation(
        "update-maintenance-record",
        (key, { arg }: { arg: { recordId: string; body: UpdatableMaintenanceRecord } }) =>
            orgId
                ? updateMaintenanceRecord({ recordId: arg.recordId, body: arg.body, orgId })
                : undefined,
        { onSuccess: invalidateMaintenanceRecords }
    )

    return { updateMaintenanceRecord: trigger, isMutating }
}

const deleteMaintenanceRecord = ({ recordId, orgId }: { recordId: string; orgId: string }) => {
    return fetchApiGateway("delete /org/:orgId/fleet/maintenance/record/:recordId", {
        params: { orgId, recordId },
    })
}

export const useDeleteMaintenanceRecord = () => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { trigger, isMutating } = useSWRMutation(
        "delete-maintenance-record",
        (key, { arg: recordId }: { arg: string }) =>
            orgId ? deleteMaintenanceRecord({ recordId, orgId }) : undefined,
        { onSuccess: invalidateMaintenanceRecords }
    )

    return { deleteMaintenanceRecord: trigger, isMutating }
}
